import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { contracts, type Contract } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { MarkdownView } from '@/components/MarkdownView';
import { formatMoney } from '@/lib/money';

export function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const contractId = id ? Number(id) : null;

  const [contract, setContract] = useState<Contract | null>(null);
  const [edited, setEdited] = useState<string>('');
  const [editAmount, setEditAmount] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [regenInstructions, setRegenInstructions] = useState('');
  const [regenOpen, setRegenOpen] = useState(false);

  const reload = () => {
    if (!contractId) return;
    setErr(null);
    contracts.get(contractId).then(c => {
      setContract(c);
      setEdited(c.contentMarkdown ?? '');
      setEditAmount(c.amount != null ? String(c.amount) : '');
    }).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => {
    setContract(null); setEdited(''); setEditAmount(''); setEditing(false);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!contract) return <div className="text-ink-400">Ładowanie…</div>;

  const isDraft = contract.draft;

  const onSaveEdit = async () => {
    setBusy(true); setErr(null); setInfo(null);
    try {
      await contracts.updateDraft(contract.id, {
        contentMarkdown: edited,
        amount: editAmount ? Number(editAmount) : undefined,
      });
      setInfo('Zapisano zmiany draftu.');
      setEditing(false);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onAccept = async () => {
    if (!confirm('Czy na pewno akceptujesz draft umowy? Po akceptacji edycja będzie zablokowana.')) return;
    setBusy(true); setErr(null); setInfo(null);
    try {
      await contracts.accept(contract.id);
      setInfo('Umowa zaakceptowana.');
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRegenerate = async () => {
    setBusy(true); setErr(null); setInfo(null); setRegenOpen(false);
    try {
      await contracts.regenerate(contract.id, regenInstructions || undefined);
      setInfo('Wygenerowano nową wersję draftu.');
      setRegenInstructions('');
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-start justify-between mb-5 gap-4">
        <div className="min-w-0">
          <div className="text-[12px] text-ink-500 mb-1">
            <NavLink to="/oferty" className="hover:text-ink-800">Oferty</NavLink>
            {contract.sourceOfferId && (
              <>
                <span className="mx-1.5">/</span>
                <NavLink to={`/oferty/${contract.sourceOfferId}`} className="hover:text-ink-800 font-mono">
                  {contract.sourceOfferNumber}
                </NavLink>
              </>
            )}
            <span className="mx-1.5">/</span>
            <span className="text-ink-700">Umowa</span>
          </div>
          <h1 className="m-0 flex items-center gap-3">
            <span>{contract.number}</span>
            {isDraft && <span className="pill pill-amber">DRAFT</span>}
            {!isDraft && contract.signed && <span className="pill pill-green">PODPISANA</span>}
            {!isDraft && !contract.signed && <span className="pill pill-water">ZAAKCEPTOWANA</span>}
            {contract.aiGenerated && <span className="pill pill-ai">AI · {contract.aiProvider}/{contract.aiModel}</span>}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-500 mt-2">
            <span>Wariant: <span className="font-semibold text-ink-700">{contract.wariantName}</span></span>
            {contract.studies.length > 0 && (
              <span>{contract.studies.length === 1 ? 'Opracowanie' : `Opracowania (${contract.studies.length})`}:{' '}
                {contract.studies.map((s, i) => (
                  <span key={s.id}>
                    {i > 0 && ', '}
                    <NavLink to={`/studies/${s.id}`} className="font-mono text-water-600 no-underline hover:underline">
                      {s.number}
                    </NavLink>
                    {contract.studies.length === 1 && s.title && <span className="text-ink-700"> — {s.title}</span>}
                  </span>
                ))}
              </span>
            )}
            {contract.amount != null && <span>Kwota: <span className="font-mono font-semibold text-ink-800">{formatMoney(contract.amount)} zł</span></span>}
            {contract.acceptedAt && (
              <span>Zaakceptowana {new Date(contract.acceptedAt).toLocaleString('pl-PL')}
                {contract.acceptedByName && <> przez <span className="font-semibold text-ink-700">{contract.acceptedByName}</span></>}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {isDraft && !editing && (
            <>
              <button onClick={() => setEditing(true)} disabled={busy} className="btn btn-secondary btn-sm">
                <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj
              </button>
              <button onClick={() => setRegenOpen(o => !o)} disabled={busy} className="btn btn-secondary btn-sm">
                <Icon name="sparkles" className="w-3.5 h-3.5"/> Regeneruj AI
              </button>
              <button onClick={onAccept} disabled={busy} className="btn btn-primary btn-sm">
                <Icon name="check" className="w-3.5 h-3.5"/> Akceptuj draft
              </button>
            </>
          )}
          {editing && (
            <>
              <button onClick={() => { setEditing(false); setEdited(contract.contentMarkdown ?? ''); }} disabled={busy} className="btn btn-secondary btn-sm">
                <Icon name="x" className="w-3.5 h-3.5"/> Anuluj
              </button>
              <button onClick={onSaveEdit} disabled={busy} className="btn btn-primary btn-sm">
                <Icon name="check" className="w-3.5 h-3.5"/> Zapisz zmiany
              </button>
            </>
          )}
          <a href={`/api/contracts/${contract.id}/pdf`} download className="btn btn-secondary btn-sm">
            <Icon name="file-text" className="w-3.5 h-3.5"/> PDF
          </a>
          <a href={`/api/contracts/${contract.id}/docx`} download className="btn btn-secondary btn-sm">
            <Icon name="file-text" className="w-3.5 h-3.5"/> DOCX
          </a>
          <NavLink to={contract.studies[0]?.id ? `/studies/${contract.studies[0].id}` : '/oferty'} className="btn btn-secondary btn-sm">← Powrót</NavLink>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {info && <div className="text-green-600 mb-3 text-[12px]">{info}</div>}

      {regenOpen && isDraft && (
        <div className="card mb-4 border-ai-500/30 bg-ai-500/5">
          <div className="card-head"><div className="card-title">Regeneracja AI z dodatkowymi instrukcjami</div></div>
          <div className="card-body flex flex-col gap-3">
            <textarea
              value={regenInstructions}
              onChange={e => setRegenInstructions(e.target.value)}
              placeholder="Np. 'Dodaj klauzulę o zaliczce 30%', 'Skróć paragraf o RODO', 'Wprowadź zapis o gwarancji 12 miesięcy'…"
              className="input min-h-[80px] font-mono text-[12px]"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRegenOpen(false)} className="btn btn-secondary btn-sm">Anuluj</button>
              <button onClick={onRegenerate} disabled={busy} className="btn btn-primary btn-sm">
                {busy ? <Spinner/> : <Icon name="sparkles" className="w-3.5 h-3.5"/>}
                {busy ? 'Generuję…' : 'Wygeneruj nową wersję'}
              </button>
            </div>
            <div className="text-[11px] text-ink-500">Bez instrukcji: standardowy prompt z RAG-iem (poprzednie umowy z bazy).</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-[1fr_320px] gap-4">
        <div className="card">
          <div className="card-head">
            <div className="card-title">{editing ? 'Edycja treści' : 'Treść umowy'}</div>
            <span className="ml-auto text-[11px] text-ink-400">Markdown · {(contract.contentMarkdown?.length ?? 0).toLocaleString('pl-PL')} znaków</span>
          </div>
          <div className="card-body">
            {editing ? (
              <>
                <textarea
                  value={edited}
                  onChange={e => setEdited(e.target.value)}
                  className="input font-mono text-[12px] leading-relaxed w-full min-h-[600px]"
                  spellCheck={false}
                />
                <div className="mt-3 flex items-center gap-3">
                  <label className="text-[12px] text-ink-700">Kwota (zł):</label>
                  <input
                    type="number"
                    value={editAmount}
                    onChange={e => setEditAmount(e.target.value)}
                    step="0.01"
                    className="input font-mono text-[12px] w-40"
                  />
                </div>
              </>
            ) : (
              contract.contentMarkdown
                ? <MarkdownView source={contract.contentMarkdown} className="font-serif"/>
                : <span className="text-ink-400 italic">Brak treści — uruchom regenerację AI.</span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="card">
            <div className="card-head"><div className="card-title">Status</div></div>
            <div className="card-body text-[12px]">
              <Row label="Numer umowy" value={contract.number} mono/>
              <Row label="Status"
                value={isDraft ? '🟡 Draft' : contract.signed ? '🟢 Podpisana' : '🔵 Zaakceptowana, nie podpisana'}/>
              <Row label="Wariant" value={contract.wariantName}/>
              <Row label="Wersja szablonu" value={contract.templateVersion ?? '—'} mono/>
              <Row label="AI" value={contract.aiGenerated
                ? `${contract.aiProvider} / ${contract.aiModel}`
                : 'ręcznie/kreator'} mono/>
              {contract.acceptedAt && (
                <Row label="Akceptacja" value={new Date(contract.acceptedAt).toLocaleString('pl-PL')
                  + (contract.acceptedByName ? ` (${contract.acceptedByName})` : '')}/>
              )}
              {contract.signedDate && <Row label="Data podpisu" value={contract.signedDate} mono/>}
            </div>
          </div>

          {contract.sourceOfferId && (
            <div className="card">
              <div className="card-head"><div className="card-title">Powiązania</div></div>
              <div className="card-body text-[12px]">
                <NavLink to={`/oferty/${contract.sourceOfferId}`} className="flex items-center gap-2 py-2 no-underline text-water-600 hover:underline">
                  <Icon name="file-text" className="w-3.5 h-3.5"/>
                  Oferta {contract.sourceOfferNumber}
                </NavLink>
                {contract.studies.map(s => (
                  <NavLink key={s.id} to={`/studies/${s.id}`}
                           className="flex items-center gap-2 py-2 no-underline text-water-600 hover:underline">
                    <Icon name="droplet" className="w-3.5 h-3.5"/>
                    Opracowanie {s.number}
                    {s.deadline && <span className="text-ink-400 text-[11px] font-mono ml-auto">{s.deadline}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head"><div className="card-title">Co dalej?</div></div>
            <div className="card-body text-[12px] text-ink-700 leading-relaxed flex flex-col gap-2">
              {isDraft && (
                <>
                  <div>1. Zapoznaj się z treścią — wszystkie paragrafy, kwoty, terminy.</div>
                  <div>2. <span className="font-semibold">Edytuj</span> ręcznie lub <span className="font-semibold">Regeneruj AI</span> z instrukcjami.</div>
                  <div>3. Po sprawdzeniu — <span className="font-semibold">Akceptuj draft</span> (blokuje edycję).</div>
                </>
              )}
              {!isDraft && !contract.signed && (
                <>
                  <div>Umowa zaakceptowana — wyślij do podpisu klientowi.</div>
                  <div className="text-ink-500">Po obustronnym podpisie: oznacz jako podpisaną w karcie umowy.</div>
                </>
              )}
              {contract.signed && <div>Umowa w pełni zatwierdzona i podpisana.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-ink-100 last:border-b-0">
      <span className="text-ink-500">{label}</span>
      <span className={'text-ink-800 ' + (mono ? 'font-mono text-[11px]' : '')}>{value}</span>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );
}
